import { useState } from 'react';

import { useRentalManagerUserExperimentWebApiHandler_getTreatment } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useIsomorphicLayoutEffect } from './useIsomorphicLayoutEffect';

import {
  getCookieAsBoolean,
  PORTFOLIO_DASHBOARD_SPLIT_FEATURE_NAME,
  ZRM_PORTFOLIO_DASHBOARD_ENABLED,
} from '../utils/featureFlags';

export type UsePortfolioDashboardEnabledParams = {
  loggedIn?: boolean;
  /** ZRAM users have their own dashboard, so they are always excluded, ahead of the opt-in cookie and Split. */
  isEnterpriseCustomer?: boolean;
};

/**
 * Portfolio Dashboard gate, shared by the nav entry in every app and zrmw's /dashboard route. The
 * experiment treatment or the opt-in cookie (honored in every environment) is the only switch. `isLoading` is for route guards.
 */
export const usePortfolioDashboardEnabled = ({
  loggedIn,
  isEnterpriseCustomer,
}: UsePortfolioDashboardEnabledParams = {}) => {
  // Read after mount: the cookie is false on the server and would mismatch on hydration.
  const [cookie, setCookie] = useState({ checked: false, enabled: false });

  useIsomorphicLayoutEffect(() => {
    setCookie({ checked: true, enabled: getCookieAsBoolean(ZRM_PORTFOLIO_DASHBOARD_ENABLED) });
  }, []);

  // `!cookie.enabled` keeps cookie opt-ins out of the experiment's exposure data.
  const isQueryEnabled = Boolean(loggedIn) && !isEnterpriseCustomer && cookie.checked && !cookie.enabled;

  const { data, isPending } = useRentalManagerUserExperimentWebApiHandler_getTreatment(
    { featureName: PORTFOLIO_DASHBOARD_SPLIT_FEATURE_NAME },
    {
      enabled: isQueryEnabled,
      staleTime: Infinity,
      throwOnError: false,
      meta: {
        tags: ['portfolio-dashboard-treatment'],
        context: { api: 'getTreatment', details: { featureName: PORTFOLIO_DASHBOARD_SPLIT_FEATURE_NAME } },
      },
    },
  );

  if (isEnterpriseCustomer) {
    return { isEnabled: false, isLoading: false };
  }

  return {
    isEnabled: cookie.enabled || data?.treatment === 'on',
    isLoading: Boolean(loggedIn) && (!cookie.checked || (isQueryEnabled && isPending)),
  };
};

export default usePortfolioDashboardEnabled;
